import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';

import { Authenticator } from '@aws-amplify/ui-react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { DAYJS_LOCAL_CONFIG, DAYJS_THRESHOLDS } from 'constants/times';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import { flowRight } from 'lodash';

import { AccessProvider } from 'components/AccessProvider/AccessProvider';
import { AuthModalsContextProvider } from 'components/AuthModals';
import { AuthProvider } from 'components/AuthProvider/AuthProvider';
import { AuthUserLimitsProvider } from 'components/AuthUserLimitsProvider/AuthUserLimitsProvider';
import { AuthUserSubscriptionProvider } from 'components/AuthUserSubscriptionProvider/AuthUserSubscriptionProvider';
import { ErrorBoundaryFallbackComponent } from 'components/ErrorBoundaryFallback/ErrorBoundaryFallbackComponent';
import { HeaderLogicProvider } from 'components/Header/HeaderLogicProvider';
import { ModalProvider } from 'components/ModalManager';
import { QueryClientProvider } from 'components/QueryClientProvider/QueryClientProvider';
import { theme } from 'components/Theme/theme';
import { ToastProvider } from 'components/ToastProvider/ToastProvider';

import { getVariable } from 'helpers/environment.utils';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale, { thresholds: DAYJS_THRESHOLDS });
dayjs.updateLocale('en', DAYJS_LOCAL_CONFIG);

interface IAppProviderProps {
  children: React.ReactNode;
}

const Providers: React.FC<IAppProviderProps> = ({ children }) => {
  const isDevelopmentOrStage =
    getVariable('REACT_APP_ENV_ROOT_PATH').includes('dev') || getVariable('REACT_APP_ENV_ROOT_PATH').includes('stage');

  return flowRight([
    (n) => <ThemeProvider theme={theme} children={n} />,
    (n) => <StyledEngineProvider injectFirst children={n} />,
    (n) => <ErrorBoundary fallback={<ErrorBoundaryFallbackComponent authenticated={false} />} children={n} />,
    (n) => <Authenticator.Provider children={n} />,
    (n) => <ToastProvider children={n} />,
    (n) => <QueryClientProvider children={n} />,
    (n) => <AuthModalsContextProvider children={n} />,
    (n) => <AuthProvider children={n} />,
    (n) => <AuthUserSubscriptionProvider children={n} />,
    (n) => <AuthUserLimitsProvider children={n} />,
    (n) => <ModalProvider children={n} />,
    (n) => <HeaderLogicProvider children={n} />,
    // @ts-ignore
    ...(isDevelopmentOrStage ? [(n) => <AccessProvider children={n} />] : []),
  ])(children);
};

export const AppProvider = () => {
  return (
    <Providers>
      <Outlet />
    </Providers>
  );
};
